import {
  RideInvestigationHistoryEntryTypeEnum,
  RideInvestigationHistorySourceEnum,
  RideInvestigationSeverityEnum,
  RideStatusEnum,
  type IRideInvestigationHistoryDto,
  type IRideInvestigationHistoryEntryDto,
  type IRideInvestigationStationDto,
} from "@ryde/shared";

import {
  INVESTIGATION_HISTORY,
  INVESTIGATION_HISTORY_PASSENGER_EVENT_KIND,
  INVESTIGATION_HISTORY_SOURCE_GROUP,
  MS_PER_SECOND,
  SECONDS_PER_MINUTE,
} from "./investigation.constants";
import type {
  IInvestigationArrivedStop,
  IInvestigationHistoryRow,
  IInvestigationLifecycleStep,
  IPlaybackEvent,
  IPlaybackModel,
  IRideInvestigationHistoryState,
  TInvestigationHistoryFilter,
  TInvestigationLifecycleStepKey,
  TPlacedPlaybackStation,
} from "./investigation.models";
import {
  buildEventMeta,
  formatDurationSec,
  formatSignedMinutes,
  parseMs,
} from "./investigation.utils";

/** "48s" under a minute, otherwise the panel's usual "8m" / "1h 05m". */
export const formatShortDuration = (seconds: number): string =>
  seconds < SECONDS_PER_MINUTE
    ? `${Math.round(Math.max(0, seconds))}s`
    : formatDurationSec(seconds);

/** Stop numbers as compact ranges: [8, 29, 30, 31] → "8, 29–31". */
export const formatStopRanges = (stopNumbers: number[]): string =>
  [...new Set(stopNumbers)]
    .sort((left, right) => left - right)
    .reduce<[number, number][]>((ranges, stopNumber) => {
      const last = ranges.at(-1);

      if (last && stopNumber === last[1] + 1) {
        last[1] = stopNumber;
      } else {
        ranges.push([stopNumber, stopNumber]);
      }

      return ranges;
    }, [])
    .map(([from, to]) => (from === to ? String(from) : `${from}–${to}`))
    .join(", ");

export const delaySeverity = (
  minutes: number | undefined,
): RideInvestigationSeverityEnum => {
  const { warning, critical } = INVESTIGATION_HISTORY.delayThresholdMinutes;

  if (minutes === undefined || minutes < warning) {
    return RideInvestigationSeverityEnum.Info;
  }

  return minutes >= critical
    ? RideInvestigationSeverityEnum.Critical
    : RideInvestigationSeverityEnum.Warning;
};

const stationsInRouteOrder = (
  model: IPlaybackModel,
): IRideInvestigationStationDto[] =>
  [...model.payload.stations].sort(
    (left, right) => left.sequence - right.sequence,
  );

/** Stops with a recorded arrival, in route order (not arrival order). */
export const buildArrivedStops = (
  model: IPlaybackModel,
): IInvestigationArrivedStop[] =>
  stationsInRouteOrder(model).flatMap(station => {
    const arrivalMs = parseMs(station.actualArrival);

    return Number.isFinite(arrivalMs)
      ? [{ station, stopNumber: station.sequence + 1, arrivalMs }]
      : [];
  });

const driveRow = (
  row: Omit<IInvestigationHistoryRow, "group" | "isSeekable">,
): IInvestigationHistoryRow => {
  const value: IInvestigationHistoryRow = {
    ...row,
    group: "drive",
    isSeekable: true,
  };

  return value;
};

const stationIdsOf = (stops: IInvestigationArrivedStop[]): number[] =>
  stops.map(stop => stop.station.id);

/**
 * Stops the vehicle reached without stopping: an arrival on record but no dwell (no stop recorded, or a zero-length
 * one). The first and last stops are left out — the ride starts and ends there — and so is every stop of a ride whose
 * provider records no stops at all, where a missing dwell says nothing.
 */
export const findPassedWithoutStopping = (
  model: IPlaybackModel,
): IRideInvestigationStationDto[] => {
  const stations = stationsInRouteOrder(model);
  const doesRecordStops = stations.some(
    station => (station.dwellSeconds ?? 0) > 0,
  );

  if (!doesRecordStops) {
    return [];
  }

  return stations
    .slice(1, -1)
    .filter(
      station =>
        Number.isFinite(parseMs(station.actualArrival)) &&
        !((station.dwellSeconds ?? 0) > 0),
    );
};

export const buildStartRow = (
  model: IPlaybackModel,
): IInvestigationHistoryRow | undefined => {
  const { actualStart, scheduledStart } = model.payload.summary;
  const startMs = parseMs(actualStart);

  if (!Number.isFinite(startMs)) {
    return undefined;
  }

  const scheduledMs = parseMs(scheduledStart);
  const offsetSec = Number.isFinite(scheduledMs)
    ? (startMs - scheduledMs) / MS_PER_SECOND
    : 0;
  const isOnTime =
    Math.abs(offsetSec) < INVESTIGATION_HISTORY.startOnTimeToleranceSec;
  let labelKey = `${INVESTIGATION_HISTORY.i18nKey}.drive.startedLate`;

  if (isOnTime) {
    labelKey = `${INVESTIGATION_HISTORY.i18nKey}.drive.startedOnTime`;
  } else if (offsetSec < 0) {
    labelKey = `${INVESTIGATION_HISTORY.i18nKey}.drive.startedEarly`;
  }

  return driveRow({
    id: "drive:started",
    tMs: startMs,
    severity: isOnTime
      ? RideInvestigationSeverityEnum.Info
      : delaySeverity(offsetSec / SECONDS_PER_MINUTE),
    labelKey,
    labelParams: { duration: formatShortDuration(Math.abs(offsetSec)) },
    iconKey: "started",
  });
};

/**
 * One row per unbroken run of on-time stops (among stops that have a schedule to compare with). A stop passed without
 * stopping is not counted as served on time — it gets its own row.
 */
export const buildOnTimeRows = ({
  arrivedStops,
  passedStationIds,
}: {
  arrivedStops: IInvestigationArrivedStop[];
  passedStationIds: ReadonlySet<number>;
}): IInvestigationHistoryRow[] => {
  const runs = arrivedStops
    .filter(
      stop =>
        stop.station.delayMinutes !== undefined &&
        !passedStationIds.has(stop.station.id),
    )
    .reduce<IInvestigationArrivedStop[][]>((groups, stop, index, scheduled) => {
      const isOnTime =
        Math.abs(stop.station.delayMinutes ?? 0) <
        INVESTIGATION_HISTORY.onTimeToleranceMinutes;
      const previous = scheduled[index - 1];
      const wasOnTime =
        previous !== undefined &&
        Math.abs(previous.station.delayMinutes ?? 0) <
          INVESTIGATION_HISTORY.onTimeToleranceMinutes;

      if (isOnTime && wasOnTime) {
        groups.at(-1)?.push(stop);
      } else if (isOnTime) {
        groups.push([stop]);
      }

      return groups;
    }, []);

  return runs.map(run =>
    driveRow({
      id: `drive:onTime:${run[0].stopNumber}`,
      tMs: run[0].arrivalMs,
      severity: RideInvestigationSeverityEnum.Info,
      labelKey: `${INVESTIGATION_HISTORY.i18nKey}.drive.onTimeStops`,
      labelParams: {
        count: run.length,
        stops: formatStopRanges(run.map(stop => stop.stopNumber)),
      },
      iconKey: "onTime",
      stationIds: stationIdsOf(run),
    }),
  );
};

/**
 * The first moment the delay reached each threshold, plus the stop with the peak delay. "First" is by time, not by
 * route order: a stop recorded out of order can be reached before the stops ahead of it on the route.
 */
export const buildDelayRows = (
  arrivedStops: IInvestigationArrivedStop[],
): IInvestigationHistoryRow[] => {
  const crossings = Object.values(
    INVESTIGATION_HISTORY.delayThresholdMinutes,
  ).flatMap(threshold => {
    const stop = arrivedStops
      .filter(candidate => (candidate.station.delayMinutes ?? 0) >= threshold)
      .reduce<IInvestigationArrivedStop | undefined>(
        (earliest, candidate) =>
          !earliest || candidate.arrivalMs < earliest.arrivalMs
            ? candidate
            : earliest,
        undefined,
      );

    return stop
      ? [
          driveRow({
            id: `drive:delay:${threshold}`,
            tMs: stop.arrivalMs,
            severity: delaySeverity(threshold),
            labelKey: `${INVESTIGATION_HISTORY.i18nKey}.drive.delayCrossed`,
            labelParams: { minutes: threshold, stop: stop.stopNumber },
            iconKey: "delay",
            meta: stop.station.name,
            stationIds: [stop.station.id],
          }),
        ]
      : [];
  });

  const peak = arrivedStops.reduce<IInvestigationArrivedStop | undefined>(
    (best, stop) =>
      (stop.station.delayMinutes ?? -Infinity) >
      (best?.station.delayMinutes ?? -Infinity)
        ? stop
        : best,
    undefined,
  );
  const peakDelay = peak?.station.delayMinutes;

  if (
    !peak ||
    delaySeverity(peakDelay) === RideInvestigationSeverityEnum.Info
  ) {
    return crossings;
  }

  return [
    ...crossings,
    driveRow({
      id: "drive:peakDelay",
      tMs: peak.arrivalMs,
      severity: delaySeverity(peakDelay),
      labelKey: `${INVESTIGATION_HISTORY.i18nKey}.drive.peakDelay`,
      labelParams: {
        delay: formatSignedMinutes(peakDelay),
        stop: peak.stopNumber,
      },
      iconKey: "peakDelay",
      meta: peak.station.name,
      stationIds: [peak.station.id],
    }),
  ];
};

/**
 * Stops with no arrival record although a later stop has one — the vehicle went past them without the stop being
 * registered. Stops after the last recorded one are left out: the ride may simply not have got there.
 */
export const buildNoArrivalRow = (
  model: IPlaybackModel,
): IInvestigationHistoryRow | undefined => {
  const stations = stationsInRouteOrder(model);
  const lastArrivedIndex = stations.findLastIndex(station =>
    Number.isFinite(parseMs(station.actualArrival)),
  );
  const missed = stations
    .slice(0, Math.max(0, lastArrivedIndex))
    .filter(station => !Number.isFinite(parseMs(station.actualArrival)));

  if (!missed.length) {
    return undefined;
  }

  const firstMissedIndex = stations.indexOf(missed[0]);
  const nextArrivalMs = stations
    .slice(firstMissedIndex)
    .map(station => parseMs(station.actualArrival))
    .find(ms => Number.isFinite(ms));

  return driveRow({
    id: "drive:noArrival",
    tMs: nextArrivalMs ?? model.startMs,
    severity: RideInvestigationSeverityEnum.Warning,
    labelKey: `${INVESTIGATION_HISTORY.i18nKey}.drive.noArrival`,
    labelParams: {
      count: missed.length,
      stops: formatStopRanges(missed.map(station => station.sequence + 1)),
    },
    iconKey: "noArrival",
    stationIds: missed.map(station => station.id),
  });
};

/**
 * One row for the stops passed without stopping. Driving past a stop nobody waits at is normal for a shuttle, so it is
 * informational — unless passengers were booked to get on or off there.
 */
export const buildPassedWithoutStoppingRow = (
  passed: IRideInvestigationStationDto[],
): IInvestigationHistoryRow | undefined => {
  if (!passed.length) {
    return undefined;
  }

  const hasBookedPassengers = passed.some(
    station => station.boardingCount > 0 || station.alightingCount > 0,
  );

  return driveRow({
    id: "drive:passedWithoutStopping",
    tMs: Math.min(...passed.map(station => parseMs(station.actualArrival))),
    severity: hasBookedPassengers
      ? RideInvestigationSeverityEnum.Warning
      : RideInvestigationSeverityEnum.Info,
    labelKey: `${INVESTIGATION_HISTORY.i18nKey}.drive.passedWithoutStopping`,
    labelParams: {
      count: passed.length,
      stops: formatStopRanges(passed.map(station => station.sequence + 1)),
    },
    iconKey: "passedWithoutStopping",
    stationIds: passed.map(station => station.id),
  });
};

/**
 * Stops recorded earlier than a stop before them on the route. On a route that passes the same place twice the
 * arrival can be taken from the wrong pass, so their times (and delays) are suspect.
 */
export const buildOutOfOrderRow = (
  arrivedStops: IInvestigationArrivedStop[],
): IInvestigationHistoryRow | undefined => {
  const outOfOrder = arrivedStops.filter((stop, index) =>
    arrivedStops
      .slice(0, index)
      .some(previous => previous.arrivalMs > stop.arrivalMs),
  );

  if (!outOfOrder.length) {
    return undefined;
  }

  return driveRow({
    id: "drive:outOfOrder",
    tMs: Math.min(...outOfOrder.map(stop => stop.arrivalMs)),
    severity: RideInvestigationSeverityEnum.Warning,
    labelKey: `${INVESTIGATION_HISTORY.i18nKey}.drive.outOfOrder`,
    labelParams: {
      count: outOfOrder.length,
      stops: formatStopRanges(outOfOrder.map(stop => stop.stopNumber)),
    },
    iconKey: "outOfOrder",
    stationIds: stationIdsOf(outOfOrder),
  });
};

/** Arrival at the route's last stop — only when that stop itself was recorded. */
export const buildArrivedRow = (
  model: IPlaybackModel,
): IInvestigationHistoryRow | undefined => {
  const lastStation = stationsInRouteOrder(model).at(-1);
  const arrivalMs = parseMs(lastStation?.actualArrival);

  if (!lastStation || !Number.isFinite(arrivalMs)) {
    return undefined;
  }

  return driveRow({
    id: "drive:arrived",
    tMs: arrivalMs,
    severity: delaySeverity(lastStation.delayMinutes),
    labelKey: `${INVESTIGATION_HISTORY.i18nKey}.drive.arrived`,
    iconKey: "arrived",
    meta:
      lastStation.delayMinutes === undefined
        ? lastStation.name
        : `${lastStation.name} · ${formatSignedMinutes(lastStation.delayMinutes)}`,
    stationIds: [lastStation.id],
  });
};

/** GPS that kept arriving well after the last stop — the driver app was left running. */
export const buildGpsAfterEndRow = (
  model: IPlaybackModel,
): IInvestigationHistoryRow | undefined => {
  const lastStation = stationsInRouteOrder(model).at(-1);
  const terminalMs = parseMs(lastStation?.actualArrival);

  if (!lastStation || !model.hasGps || !Number.isFinite(terminalMs)) {
    return undefined;
  }

  const pointsAfter = Array.from(model.pointTimesMs).filter(
    pointMs => pointMs > terminalMs,
  );
  const lastPointMs = pointsAfter.at(-1);

  if (
    lastPointMs === undefined ||
    lastPointMs - terminalMs <
      INVESTIGATION_HISTORY.gpsAfterEndMinSec * MS_PER_SECOND
  ) {
    return undefined;
  }

  return driveRow({
    id: "drive:gpsAfterEnd",
    tMs: pointsAfter[0],
    severity: RideInvestigationSeverityEnum.Warning,
    labelKey: `${INVESTIGATION_HISTORY.i18nKey}.drive.gpsAfterEnd`,
    labelParams: {
      count: pointsAfter.length,
      duration: formatShortDuration((lastPointMs - terminalMs) / MS_PER_SECOND),
    },
    iconKey: "gpsAfterEnd",
    stationIds: [lastStation.id],
  });
};

/** GPS gaps, detours, unexpected stops and incidents, as the Events tab reports them. */
export const buildDriveEventRows = (
  model: IPlaybackModel,
): IInvestigationHistoryRow[] => {
  const driveEventTypes: readonly string[] =
    INVESTIGATION_HISTORY.driveEventTypes;

  return model.events
    .filter(({ event }) => driveEventTypes.includes(event.type))
    .map(({ event, tMs }) =>
      driveRow({
        id: `event:${event.id}`,
        tMs,
        severity: event.severity,
        labelKey: event.labelKey,
        iconKey: event.type,
        meta: [event.stationName, buildEventMeta(event)]
          .filter(Boolean)
          .join(" · "),
        stationIds: event.stationId ? [event.stationId] : undefined,
      }),
    );
};

/** Passenger names for a condensed row: the first few, then how many more. */
export const formatPassengerNames = (names: (string | undefined)[]): string => {
  const known = names.filter((name): name is string => !!name);
  const shown = known.slice(0, INVESTIGATION_HISTORY.maxNamesShown);
  const hiddenCount = known.length - shown.length;

  return hiddenCount > 0
    ? `${shown.join(", ")} +${hiddenCount}`
    : shown.join(", ");
};

/**
 * Boardings and no-shows: one row per stop and kind rather than one per passenger, so a full bus does not bury the
 * rest of the drive. An event whose pickup stop is not on this ride keeps a row of its own.
 */
export const buildPassengerEventRows = (
  model: IPlaybackModel,
): IInvestigationHistoryRow[] => {
  const stopNumberById = new Map(
    model.payload.stations.map(station => [station.id, station.sequence + 1]),
  );
  const groups = model.events.reduce<Map<string, IPlaybackEvent[]>>(
    (byStop, entry) => {
      const kind = INVESTIGATION_HISTORY_PASSENGER_EVENT_KIND[entry.event.type];

      if (!kind) {
        return byStop;
      }

      const stationId = entry.event.stationId;
      const key =
        stationId && stopNumberById.has(stationId)
          ? `${kind}:${stationId}`
          : `${kind}:event:${entry.event.id}`;

      byStop.set(key, [...(byStop.get(key) ?? []), entry]);

      return byStop;
    },
    new Map(),
  );

  return [...groups.values()].map(group => {
    const [first] = [...group].sort((left, right) => left.tMs - right.tMs);
    const kind =
      INVESTIGATION_HISTORY_PASSENGER_EVENT_KIND[first.event.type] ?? "boarded";
    const stationId = first.event.stationId;
    const stopNumber = stationId ? stopNumberById.get(stationId) : undefined;
    const severity =
      kind === "noShow"
        ? RideInvestigationSeverityEnum.Warning
        : RideInvestigationSeverityEnum.Info;

    if (stationId === undefined || stopNumber === undefined) {
      return driveRow({
        id: `event:${first.event.id}`,
        tMs: first.tMs,
        severity,
        labelKey: first.event.labelKey,
        iconKey: first.event.type,
        meta: [first.event.passengerName, buildEventMeta(first.event)]
          .filter(Boolean)
          .join(" · "),
      });
    }

    return driveRow({
      id: `drive:${kind}:${stationId}`,
      tMs: first.tMs,
      severity,
      labelKey: `${INVESTIGATION_HISTORY.i18nKey}.drive.${kind}`,
      labelParams: { count: group.length, stop: stopNumber },
      iconKey: kind,
      meta: formatPassengerNames(group.map(entry => entry.event.passengerName)),
      stationIds: [stationId],
    });
  });
};

/** The drive condensed to the moments worth reading, from the investigation payload already loaded. */
export const buildDriveRows = (
  model: IPlaybackModel,
): IInvestigationHistoryRow[] => {
  const arrivedStops = buildArrivedStops(model);
  const passed = findPassedWithoutStopping(model);
  const passedStationIds = new Set(passed.map(station => station.id));

  return [
    buildStartRow(model),
    ...buildOnTimeRows({ arrivedStops, passedStationIds }),
    ...buildDelayRows(arrivedStops),
    buildNoArrivalRow(model),
    buildPassedWithoutStoppingRow(passed),
    buildOutOfOrderRow(arrivedStops),
    buildArrivedRow(model),
    buildGpsAfterEndRow(model),
    ...buildDriveEventRows(model),
    ...buildPassengerEventRows(model),
  ].filter((row): row is IInvestigationHistoryRow => row !== undefined);
};

/** The placed stations the selected Timeline row points at, ringed on the map. */
export const selectHighlightedStations = ({
  stations,
  stationIds,
}: {
  stations: TPlacedPlaybackStation[];
  stationIds: readonly number[];
}): TPlacedPlaybackStation[] => {
  if (!stationIds.length) {
    return [];
  }

  const ids = new Set(stationIds);

  return stations.filter(entry => ids.has(entry.station.id));
};

export const buildEntryRows = ({
  entries,
  startMs,
  endMs,
}: {
  entries: IRideInvestigationHistoryEntryDto[];
  startMs: number;
  endMs: number;
}): IInvestigationHistoryRow[] =>
  entries.flatMap(entry => {
    const tMs = parseMs(entry.timestamp);

    if (!Number.isFinite(tMs)) {
      return [];
    }

    const row: IInvestigationHistoryRow = {
      id: entry.id,
      group: INVESTIGATION_HISTORY_SOURCE_GROUP[entry.source],
      tMs,
      severity: entry.severity,
      labelKey: entry.labelKey,
      iconKey: entry.type,
      isSeekable: tMs >= startMs && tMs <= endMs,
      entry,
    };

    return [row];
  });

/** Drive moments and backend entries in one list, oldest first (ties keep a stable order by id). */
export const buildHistoryRows = ({
  model,
  history,
}: {
  model: IPlaybackModel;
  history?: IRideInvestigationHistoryDto;
}): IInvestigationHistoryRow[] =>
  [
    ...buildDriveRows(model),
    ...buildEntryRows({
      entries: history?.entries ?? [],
      startMs: model.startMs,
      endMs: model.endMs,
    }),
  ].sort(
    (left, right) =>
      left.tMs - right.tMs ||
      // Numeric ids compare as numbers, so rows at the same instant keep the order they were written in.
      left.id.localeCompare(right.id, undefined, { numeric: true }),
  );

export const countHistoryRows = (
  rows: IInvestigationHistoryRow[],
): Record<TInvestigationHistoryFilter, number> => {
  const empty: Record<TInvestigationHistoryFilter, number> = {
    all: rows.length,
    drive: 0,
    changes: 0,
    supplier: 0,
    bulk: 0,
  };

  return rows.reduce(
    (counts, row) => ({ ...counts, [row.group]: counts[row.group] + 1 }),
    empty,
  );
};

export const filterHistoryRows = ({
  rows,
  filter,
}: {
  rows: IInvestigationHistoryRow[];
  filter: TInvestigationHistoryFilter;
}): IInvestigationHistoryRow[] =>
  filter === "all" ? rows : rows.filter(row => row.group === filter);

/** The last replayable row the playhead has reached (rows are time-sorted). */
export const findActiveHistoryRowId = ({
  rows,
  currentMs,
}: {
  rows: IInvestigationHistoryRow[];
  currentMs?: number;
}): string | undefined => {
  if (currentMs === undefined) {
    return undefined;
  }

  return rows.findLast(row => row.isSeekable && row.tMs <= currentMs)?.id;
};

const firstEntryMs = (
  entries: IRideInvestigationHistoryEntryDto[],
  predicate: (entry: IRideInvestigationHistoryEntryDto) => boolean,
): number | undefined => {
  const match = entries.find(predicate);

  return match ? parseMs(match.timestamp) : undefined;
};

const isStatusChangeTo = (
  entry: IRideInvestigationHistoryEntryDto,
  statuses: readonly RideStatusEnum[],
): boolean =>
  entry.type === RideInvestigationHistoryEntryTypeEnum.StatusChanged &&
  entry.audit?.status !== undefined &&
  statuses.includes(entry.audit.status);

/**
 * When the ride was sent: its first audited change to a dispatched status (Sent, or Received when the supplier
 * confirmed at once). Supplier calls do not count — a supplier can reject a booking inside an HTTP 200 (Kastel's
 * `Msg`, YIT's `response_code`) — while the ride reaches those statuses only once the dispatch went through.
 */
export const findSentMs = (
  entries: IRideInvestigationHistoryEntryDto[],
): number | undefined =>
  firstEntryMs(entries, entry =>
    isStatusChangeTo(entry, INVESTIGATION_HISTORY.dispatchedStatuses),
  );

/**
 * When the ride was closed: the time of its latest status change, when that change closed it. A ride reopened after
 * a cancellation has a later, non-closing change, so the old cancellation no longer counts.
 */
export const findClosedMs = (
  entries: IRideInvestigationHistoryEntryDto[],
): number | undefined => {
  const lastStatusChange = entries.findLast(
    entry =>
      entry.type === RideInvestigationHistoryEntryTypeEnum.StatusChanged &&
      entry.audit?.status !== undefined,
  );

  return lastStatusChange &&
    isStatusChangeTo(lastStatusChange, INVESTIGATION_HISTORY.closedStatuses)
    ? parseMs(lastStatusChange.timestamp)
    : undefined;
};

/**
 * Created → Sent → Started → Arrived → Closed. A step is `missing` when a later step happened without it, `unknown`
 * when it would be missing but the audit trail that proves it is incomplete, and `pending` when nothing after it has
 * happened yet. Closed follows the ride's current status — a reopened ride is not
 * closed, whatever its history says — and has no later step, so it is `missing` once the ride arrived longer ago than
 * the close job's grace period: a ride left open.
 */
export const buildLifecycleSteps = ({
  model,
  history,
  nowMs,
}: {
  model: IPlaybackModel;
  history?: IRideInvestigationHistoryDto;
  nowMs: number;
}): IInvestigationLifecycleStep[] => {
  const entries = history?.entries ?? [];
  const lastStation = stationsInRouteOrder(model).at(-1);
  const closedStatuses: readonly string[] =
    INVESTIGATION_HISTORY.closedStatuses;
  const status = model.payload.summary.status;
  const isClosedByStatus = closedStatuses.includes(status);

  const stepMs: Record<TInvestigationLifecycleStepKey, number | undefined> = {
    created: firstEntryMs(
      entries,
      entry => entry.type === RideInvestigationHistoryEntryTypeEnum.RideCreated,
    ),
    sent: findSentMs(entries),
    started: parseMs(model.payload.summary.actualStart),
    arrived: parseMs(lastStation?.actualArrival),
    closed: isClosedByStatus ? findClosedMs(entries) : undefined,
  };
  const notDrivenStatuses: readonly string[] =
    INVESTIGATION_HISTORY.notDrivenStatuses;
  const optionalSteps: readonly string[] =
    INVESTIGATION_HISTORY.notDrivenOptionalSteps;
  // A cancelled ride was never meant to be driven, so a missing send/start/arrival is not a gap.
  const canBeMissing = (key: TInvestigationLifecycleStepKey): boolean =>
    !(notDrivenStatuses.includes(status) && optionalSteps.includes(key));
  const isDone = (key: TInvestigationLifecycleStepKey): boolean =>
    key === "closed" ? isClosedByStatus : Number.isFinite(stepMs[key] ?? NaN);
  // A step only the audit trail can prove is not "missing" when that trail was capped or failed to load.
  const incompleteSources = [
    ...(history?.limitedSources ?? []),
    ...(history?.unavailableSources ?? []),
  ];
  const isAuditIncomplete = incompleteSources.includes(
    RideInvestigationHistorySourceEnum.Audit,
  );
  const auditOnlySteps: readonly string[] =
    INVESTIGATION_HISTORY.auditOnlySteps;

  return INVESTIGATION_HISTORY.lifecycleSteps.map((key, index) => {
    const arrivedMs = stepMs.arrived ?? NaN;
    const isLeftOpen =
      key === "closed" &&
      nowMs - arrivedMs > INVESTIGATION_HISTORY.closeGraceMs;
    const hasLaterStep =
      isLeftOpen ||
      INVESTIGATION_HISTORY.lifecycleSteps
        .slice(index + 1)
        .some(laterKey => isDone(laterKey));
    let state: IInvestigationLifecycleStep["state"] = "pending";

    if (isDone(key)) {
      state = "done";
    } else if (hasLaterStep && canBeMissing(key)) {
      state =
        isAuditIncomplete && auditOnlySteps.includes(key)
          ? "unknown"
          : "missing";
    }

    const tMs = stepMs[key];
    const step: IInvestigationLifecycleStep = {
      key,
      state,
      tMs: tMs !== undefined && Number.isFinite(tMs) ? tMs : undefined,
    };

    return step;
  });
};

/** What stays on screen while a ride's history reloads or after a reload failed: only that same ride's last answer. */
export const getRetainedHistory = (
  state: IRideInvestigationHistoryState,
  rideId: number,
): IRideInvestigationHistoryDto | undefined =>
  state.rideId === rideId ? state.history : undefined;
